import 'dayjs/locale/en-gb';

import dayjs from 'dayjs';
import posthog from 'posthog-js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import App from './app';
import MainLayout from './layouts/main';
import { queryClient } from './api/query-client';
import { routesSection } from './routes/sections';
import { ErrorBoundary } from './routes/components';
import { applyStoredConsent } from './utils/analytics';

const isAnalyticsEnabled = import.meta.env.VITE_POSTHOG_KEY && import.meta.env.PROD;

if (isAnalyticsEnabled) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    opt_out_capturing_by_default: true,
  });
  applyStoredConsent();
}

const router = createBrowserRouter([
  {
    Component: () => (
      <App>
        <MainLayout>
          <Outlet />
        </MainLayout>
      </App>
    ),
    errorElement: <ErrorBoundary />,
    children: routesSection,
  },
]);

dayjs.locale('en-gb');

const root = createRoot(document.getElementById('root')!);

root.render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <RouterProvider router={router} />
        </LocalizationProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>
);
